import { TranslationKey } from './../../../../../types/translation/TranslationKey';
import React, { useContext, useState } from 'react';
import { t } from '../../../../../types/translation/Translator';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { CsvUploadConfiguration, CsvUploadTemplate, CsvUploadType } from '../../../../../types/csvUploadConfiguration';
import {
  CsvUploadConfigurationQueries,
  GetCsvUploadConfigurationForFileResponse,
  GetCsvUploadConfigurationForFileVariables,
} from '../../../../../graphql/csvUploadConfiguration.graphql';
import { useLazyQuery } from '@apollo/client';
import { Base64 } from 'js-base64';
import CreateCsvUploadConfigurationMapping from '../../../../components/CsvUploadConfiguration/CreateCsvUploadConfigurationMapping';
import { CsvUploadConfigurationSelect } from '../../../../components/CsvUploadConfiguration/CsvUploadConfigurationSelect';
import { Grid } from '@mui/material';
import UploadCsvInput from '../../../Common/UploadCsvInput';
import { CompanyRoleAssignmentContext } from '../../../../../context/CompanyRoleAssignmentContext';
import CsvUploadConfigurationInspect from '../../../../components/CsvUploadConfiguration/CsvUploadConfigurationInspect';
import Modal from '../../../../../VentoryUI/components/common/Modal/Modal';
import ModalPane from '../../../../../VentoryUI/components/common/Modal/components/ModalPane';
import { Button, ButtonVariant, CancelButton, NextButton } from '../../../../components/Button';

enum ImportOrderStatus {
  fileUpload,
  selectConfig,
  createConfig,
  inspect,
}

interface ImportOrderModalInputProps {
  open: boolean;
  setOpen: (value: boolean) => void;
}

export default function ImportOrderModal({ open, setOpen }: ImportOrderModalInputProps) {
  const { currentCompany } = useContext(CompanyContext);
  const { hasCompanyRole } = useContext(CompanyRoleAssignmentContext);

  const [file, setFile] = useState<File | null>(null);
  const [status, setStatus] = useState<ImportOrderStatus>(ImportOrderStatus.fileUpload);

  const [configurations, setConfigurations] = useState<CsvUploadConfiguration[]>([]);
  const [otherConfigurations, setOtherConfigurations] = useState<CsvUploadConfiguration[]>([]);
  const [fileHeaders, setFileHeaders] = useState<string[]>([]);
  const [requiredHeaders, setRequiredHeaders] = useState<string[]>([]);
  const [optionalHeaders, setOptionalHeaders] = useState<string[]>([]);
  const [fileAsBase64, setFileAsBase64] = useState<string | null>(null);

  const [error, setError] = useState<string>('');
  const [inspectId, setInspectId] = useState<string>();

  const [getConfigurations, { loading: getConfigurationsLoading }] = useLazyQuery<
    GetCsvUploadConfigurationForFileResponse,
    GetCsvUploadConfigurationForFileVariables
  >(CsvUploadConfigurationQueries.getForFile, {
    onCompleted: res => {
      setConfigurations(res.csvConfigurationsForFile.configurations.map(c => new CsvUploadConfiguration(c)));
      setFileHeaders(res.csvConfigurationsForFile.fileHeaders);

      setRequiredHeaders([...res.csvConfigurationsForFile.requiredHeaders]);

      const optional = res.csvConfigurationsForFile.optionalHeaders;
      setOptionalHeaders(optional);
      setOtherConfigurations(res.csvConfigurationsForFile.otherConfigurations);

      if (
        !res.csvConfigurationsForFile.configurations.length &&
        !res.csvConfigurationsForFile.otherConfigurations.length
      ) {
        setStatus(ImportOrderStatus.createConfig);
      } else {
        setStatus(ImportOrderStatus.selectConfig);
      }
    },
    onError: err => setError(err.message),
  });

  const handleFile = (files: FileList | null) => {
    if (!files) {
      setFile(null);
      return;
    }
    setFile(files[0]);
  };

  const handleFileUpload = async () => {
    if (!file) return;

    let asBase64 = Base64.encode(await file.text());

    await getConfigurations({
      variables: {
        companyId: currentCompany.id,
        type: CsvUploadType.order,
        csvFileAsBase64Data: asBase64,
      },
    });
    setFileAsBase64(asBase64);
  };

  const handleClose = () => {
    setFile(null);
    setStatus(ImportOrderStatus.fileUpload);
    setConfigurations([]);
    setOtherConfigurations([]);
    setFileHeaders([]);
    setRequiredHeaders([]);
    setOptionalHeaders([]);
    setFileAsBase64(null);
    setOpen(false);
    setError('');
  };

  const handleBack = () => {
    setError('');
    switch (status) {
      case ImportOrderStatus.fileUpload:
        return;
      case ImportOrderStatus.selectConfig:
        return setStatus(ImportOrderStatus.fileUpload);
      case ImportOrderStatus.createConfig:
        return setStatus(ImportOrderStatus.fileUpload);
      case ImportOrderStatus.inspect:
        return setStatus(ImportOrderStatus.selectConfig);
    }
  };

  const content = () => {
    if (status === ImportOrderStatus.createConfig && fileAsBase64) {
      return (
        <CreateCsvUploadConfigurationMapping
          handleUploadError={newConfiguration => {
            if (newConfiguration) setConfigurations([...configurations, newConfiguration]);
            setStatus(ImportOrderStatus.selectConfig);
          }}
          onClose={handleClose}
          onBack={handleBack}
          type={CsvUploadType.order}
          required={requiredHeaders}
          optional={optionalHeaders}
          headers={fileHeaders}
          csvFileAsBase64Data={fileAsBase64}
          setError={setError}
        />
      );
    }

    if (status === ImportOrderStatus.selectConfig && fileAsBase64) {
      return (
        <CsvUploadConfigurationSelect
          deleteExisting={false}
          otherConfigurations={otherConfigurations}
          onCreate={() => setStatus(ImportOrderStatus.createConfig)}
          onClose={handleClose}
          onBack={handleBack}
          csvFileAsBase64Data={fileAsBase64}
          type={CsvUploadType.order}
          configurations={configurations}
          setError={setError}
          onInspect={id => {
            setInspectId(id);
            setStatus(ImportOrderStatus.inspect);
          }}
        />
      );
    }

    if (status === ImportOrderStatus.inspect) {
      return (
        <CsvUploadConfigurationInspect
          headers={fileHeaders}
          configuration={otherConfigurations.find(c => c.id === inspectId)}
          onBack={handleBack}
        />
      );
    }

    return (
      <ModalPane
        footer={
          <div className='flex justify-between gap-2'>
            <a
              style={{ textDecoration: 'none' }}
              href={`${process.env.REACT_APP_STORAGE_URL}/ventory/${CsvUploadTemplate.order}`}
              download={CsvUploadTemplate.order}
            >
              <Button
                variant={ButtonVariant.secondary}
                onClick={() => {}}
                text={t(TranslationKey.downloadTemplate).singular.label}
              />
            </a>
            <div className='flex gap-2'>
              <CancelButton disabled={getConfigurationsLoading} onClick={handleClose} />
              <NextButton disabled={!file} onClick={handleFileUpload} loading={getConfigurationsLoading} />
            </div>
          </div>
        }
      >
        <Grid container height={'100%'} alignContent={'space-between'}>
          <Grid item xs={12}>
            <Grid container rowSpacing={1}>
              <Grid item xs={12}>
                <UploadCsvInput file={file} onFile={handleFile} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </ModalPane>
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      error={error}
      height='650px'
      width='70%'
      title={t(TranslationKey.importOrders).singular.label}
    >
      {content()}
    </Modal>
  );
}
